import { createServerFn } from '@tanstack/react-start'
import { generateSlugWithDate } from '../shared/crudFactory'
import {
  createMultipartUpload,
  uploadPartToS3,
  completeMultipartUpload,
  abortMultipartUpload,
  getPresignedMediaUrl,
} from '../../lib/s3'
import { mediaKindFromKey } from '../../lib/noteMedia'
import {
  startMediaUploadSchema,
  uploadMediaPartSchema,
  finishMediaUploadSchema,
  abortMediaUploadSchema,
} from './mediaSchemas'
import type { UploadedMedia } from './mediaSchemas'

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'video/x-m4v': 'm4v',
}

function extensionFor(fileName: string, contentType: string): string {
  const fromName = fileName.split('.').pop()?.toLowerCase()
  if (
    fromName &&
    /^[a-z0-9]{2,5}$/.test(fromName) &&
    fromName !== fileName.toLowerCase()
  ) {
    return fromName
  }
  return (
    CONTENT_TYPE_EXTENSIONS[contentType] ??
    (contentType.startsWith('video/') ? 'mp4' : 'jpg')
  )
}

// Files upload through the app server in ~8MB chunks that stream into an S3
// multipart upload, so there is no overall file-size limit and the server
// never buffers more than one chunk. Flow: start → part × N → finish.

export const startMediaUpload = createServerFn({ method: 'POST' })
  .inputValidator(startMediaUploadSchema)
  .handler(
    async (ctx): Promise<{ key: string; uploadId: string; kind: 'image' | 'video' }> => {
      const { fileName, contentType } = ctx.data
      const baseName = fileName.replace(/\.[^.]+$/, '') || 'media'
      const ext = extensionFor(fileName, contentType)
      const key = `notes-media/${generateSlugWithDate(baseName)}.${ext}`
      const uploadId = await createMultipartUpload(key, contentType)
      return { key, uploadId, kind: mediaKindFromKey(key) }
    },
  )

export const uploadMediaPart = createServerFn({ method: 'POST' })
  .inputValidator(uploadMediaPartSchema)
  .handler(async (ctx): Promise<{ etag: string }> => {
    const { key, uploadId, partNumber, dataBase64 } = ctx.data
    const etag = await uploadPartToS3(
      key,
      uploadId,
      partNumber,
      Buffer.from(dataBase64, 'base64'),
    )
    return { etag }
  })

export const finishMediaUpload = createServerFn({ method: 'POST' })
  .inputValidator(finishMediaUploadSchema)
  .handler(async (ctx): Promise<UploadedMedia> => {
    const { key, uploadId, parts } = ctx.data
    await completeMultipartUpload(key, uploadId, parts)
    const url = await getPresignedMediaUrl(key)
    return { key, url, kind: mediaKindFromKey(key) }
  })

export const abortMediaUpload = createServerFn({ method: 'POST' })
  .inputValidator(abortMediaUploadSchema)
  .handler(async (ctx): Promise<{ success: boolean }> => {
    await abortMultipartUpload(ctx.data.key, ctx.data.uploadId)
    return { success: true }
  })
